import type { Order } from '@shared-types/common.types';
import type { RoundPhase } from '@shared-types/match.types';
import type { Statistic, StatisticDefinition, CompetitionSeasonStatistic } from '@shared-types/stats.types';
import { loadFromAPIs, recursiveCall } from '@utilities/fetch';
import { apiKey, compStatsApiUrl, matchStatsApiUrl } from '@utilities/WindowContext';

export type StatisticApiParams = {
  stats: string[];
  competitionId?: string;
  seasonYear?: string;
  phase?: RoundPhase | 'ALL';
  optionalFields?: ('PLAYER' | 'TEAM' | 'AVERAGE')[];
  order?: Order;
  limit?: number;
  offset?: number;
};

const fetchStats = (endpoint: string, params: StatisticApiParams, signal: AbortSignal) =>
  recursiveCall<Statistic>(
    endpoint,
    {
      ...params,
      stats: params.stats.join(','), // we need to keep the order here
      optionalFields: params.optionalFields ?? ['PLAYER', 'TEAM'],
    },
    {
      apiUrl: compStatsApiUrl,
      apiKey,
      signal,
    },
  );

export const fetchPlayerStats = (params: StatisticApiParams, signal?: AbortSignal) =>
  fetchStats(`v1/player-ranking`, params, signal);

export const fetchTeamStats = (params: StatisticApiParams, signal?: AbortSignal) =>
  fetchStats(`v1/team-ranking`, params, signal);

export const fetchStatsDefinitions = (signal?: AbortSignal) =>
  loadFromAPIs<StatisticDefinition[]>(
    'v1/definitions',
    {},
    {
      apiUrl: compStatsApiUrl,
      apiKey,
      signal,
    },
  );

export const fetchHeadToHead = (
  {
    competitionId,
    team1Id,
    team2Id,
  }: {
    competitionId: string | string[];
    team1Id: string;
    team2Id: string;
    toDate?: string;
  },
  signal?: AbortSignal,
) =>
  loadFromAPIs(
    'v1/team-statistics/head2head',
    {
      teamId: `${team1Id},${team2Id}`,
      competitionId,
    },
    {
      apiKey,
      apiUrl: compStatsApiUrl,
      signal,
    },
  );

export const fetchSinglePlayerStats = (
  params: {
    stats: string[];
    playerId: string;
    competitionId: string;
    seasonYear: string;
    phase: RoundPhase;
    optionalFields?: ('PLAYER' | 'TEAM' | 'AVERAGE')[];
  },
  signal?: AbortSignal,
) => {
  return loadFromAPIs<Statistic[]>(
    'v1/player-statistics',
    {
      ...params,
      stats: params.stats.join(','), // we need to keep the order here
      optionalFields: params.optionalFields ?? ['PLAYER', 'TEAM'],
    },
    {
      apiKey,
      apiUrl: compStatsApiUrl,
      signal,
    },
  );
};

export const fetchMatchStats = (matchId: string, playerId?: string, signal?: AbortSignal) =>
  loadFromAPIs<Statistic[]>(
    `v1/player-statistics/${matchId}`,
    {
      playerId,
    },
    {
      apiUrl: matchStatsApiUrl,
      apiKey,
      signal,
    },
  );

export const fetchCompetitionSeasonStats = async (
  params: { stats: string[]; playerId: string; phase?: string; competitionIds?: string[] },
  signal?: AbortSignal,
) =>
  (
    await loadFromAPIs<{ competitionSeasonStatistics: CompetitionSeasonStatistic[] }>(
      'v1/player-statistics/all-time',
      params,
      {
        apiKey,
        apiUrl: compStatsApiUrl,
        signal,
      },
    )
  )?.competitionSeasonStatistics ?? [];
